import React, { useState, useEffect, useMemo, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';

import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';

import CloseIcon from '@material-ui/icons/Close';
import LeftIcon from '@material-ui/icons/ArrowBack';
import RightIcon from '@material-ui/icons/ArrowForward';
import FolderIcon from '@material-ui/icons/Folder';
import DeleteIcon from '@material-ui/icons/Delete';

import * as Icons from '@material-ui/icons'

import Loading from 'app/components/loaders/Loading';
import FileTypes from 'app/utils/FileTypes';
import VaporExtension from 'app/utils/VaporExtension';

import useApi from 'app/context/useApi';

function FolderListItem(props) {

    const { folder, onOpen } = props;

    return (
        <ListItem
            button
            onClick={() => onOpen(folder.id)}
            ContainerComponent="div"
            >
            <ListItemIcon>
                <FolderIcon />
            </ListItemIcon>
            <ListItemText primary={folder.name}/>
            <ListItemSecondaryAction>
                <IconButton aria-label="Open Folder" onClick={() => onOpen(folder.id)}>
                    <RightIcon fontSize="small"/>
                </IconButton>
            </ListItemSecondaryAction>
        </ListItem>
    )
}

function FileListItem(props) {

    const { file, selectedFile, onSelect, type } = props;

    const allowed = useMemo(()=> {
        if (type) {
            console.log(type);
            switch (type) {
                case 'image':
                    const fileType = FileTypes.find(obj => obj.ext === file.extension);
                    if (fileType && fileType.icon === 'Image') return true;
                default: return false;
            }
        }

        return true;
    }, [file]);

    const Icon = useMemo(() => {
        if ('extension' in file) {
            const fileType = FileTypes.find(obj => obj.ext === file.extension);
            return Icons[fileType?.icon ?? 'Description'];
        } else return Icons['Folder'];

    }, [file]);

    return (
        <ListItem
            button
            onClick={() => onSelect(file)}
            selected={(selectedFile?.id === file.id)}
            disabled={(! allowed)}
            >
            <ListItemIcon>
                <Icon />
            </ListItemIcon>
            <ListItemText primary={file.name}/>
        </ListItem>
    )
}

function NewFileListItem(props) {

    const { file, onRemove, uploadProgress, index, errors } = props;

    const Icon = useMemo(() => {
        if ('type' in file) {
            const fileType = FileTypes.find(obj => obj.mime === file.type);
            return Icons[fileType?.icon ?? 'Description'];
        } else return Icons['Folder'];

    }, [file]);

    return (
        <ListItem ContainerComponent="div">
            <ListItemIcon>
                <Icon />
            </ListItemIcon>
            <ListItemText
                primary={file.name}
                secondary={errors[index] ?? null}
                secondaryTypographyProps={{ color: 'error' }}
                />
            <ListItemSecondaryAction>
                {(uploadProgress[index] !== undefined) ? (
                    <CircularProgress variant="determinate" value={uploadProgress[index]} />
                ) : (
                    <IconButton aria-label="Remove File" onClick={onRemove}>
                        <DeleteIcon fontSize="small"/>
                    </IconButton>
                )}
            </ListItemSecondaryAction>
        </ListItem>
    )
}

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    dialogTitle: {
        padding: 0
    },
    listItemTitle: {
        paddingLeft: 6
    },
    dialogContent: {
        backgroundColor: theme.palette.grey[100],
        padding: 0
    },
    folderContainer: {
        position: 'relative',
        minHeight: 300,
        overFlowY: 'scroll',
        overflowX: 'hidden'
    },
    folder: {
        position: 'absolute',
        width: '100%',
        left: '100%',
        height: '100%'
    },
    folderEmptyContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%'
    },
    input: {
        display: 'none',
    },
}));

const transitionTime = 200;
const transition = `${transitionTime}ms ease left`;

function slideLeftIn(id) {
    const f = document.getElementById(`move-folder-${id}`);

    f.style.left = '100%';

    setTimeout(() => {
        f.style.transition = transition;
        f.style.left = '0%';

        setTimeout(() => {
            f.style.transition = null;
        }, transitionTime);
    }, 10);
}

function slideLeftOut(id) {
    const f = document.getElementById(`move-folder-${id}`);

    setTimeout(() => {
        f.style.transition = transition;
        f.style.left = '-100%';

        setTimeout(() => {
            f.style.transition = null;
        }, transitionTime);
    }, 10);
}

function slideRightIn(id) {
    const f = document.getElementById(`move-folder-${id}`);

    f.style.left = '-100%';

    setTimeout(() => {
        f.style.transition = transition;
        f.style.left = '0%';

        setTimeout(() => {
            f.style.transition = null;
        }, transitionTime);
    }, 10);
}

function slideRightOut(id) {
    const f = document.getElementById(`move-folder-${id}`);

    setTimeout(() => {
        f.style.transition = transition;
        f.style.left = '100%';

        setTimeout(() => {
            f.style.transition = null;
        }, transitionTime);
    }, 10);
}

const clubFolder = {id: 0, name: 'Club Drive', folder_id: null};

export default function SelectFileDialog(props) {

    const classes = useStyles();
    const api = useApi();
    const fileInputField = useRef(null);

    const { open, onClose, onSelect, onClosed, type, visibility="public", restrict } = props;

    const [ folders, setFolders ] = useState([clubFolder]);
    const [ files, setFiles ] = useState([]); // Files to be uploaded

    const [ uploadProgress, setUploadProgress ] = useState({});
    const [ filesLeftToBeUploaded, setFilesLeftToBeUploaded ] = useState(0);

    const [ selectedFile, setSelectedFile ] = useState(null);
    const [ activeFolderId, setActiveFolderId ] = useState(0);
    const [ uploadingFiles, setUploadingFiles ] = useState(false);

    const [ errors, setErrors ] = useState({});
    const [ loading, setLoading ] = useState(false);

    // Getters ------------------------------------

    const getFilesAndFolders = () => {
        setLoading(true);

        api.post('/admin/drive')
            .then(res => {
                const newFolders = res.folders;
                const newClubFolder = {...clubFolder};

                newClubFolder.folders = newFolders.filter(f => f.folder_id === 0);
                newClubFolder.files = res.files;

                newFolders.push(newClubFolder);

                setFolders(newFolders);
            }).finally(() => setLoading(false));
    }

    useEffect(() => {
        getFilesAndFolders();
    }, []);

    // Dialog Functions -----------------------------------------------

    const handleExit = () => {

        setLoading(false);
        setUploadingFiles(false);
        setUploadProgress({});
        setFilesLeftToBeUploaded(0);
        setSelectedFile(null);
        setActiveFolderId(0);
        setFiles([]);

        if (activeFolderId > 0) slideRightOut(activeFolderId);
        if (onClosed) onClosed();
    }

    // Calculated Variables

    const activeFolder = useMemo(() => {
        return  folders.find(folder => folder.id === activeFolderId) ?? clubFolder;
    }, [activeFolderId, folders])

    // Setter Functions -----------------------------------------------

    const handleFolderNext = id => {
        setActiveFolderId(id);
        slideLeftOut(activeFolderId);
        slideLeftIn(id);
    }

    const handleFolderBack = () => {
        if (uploadingFiles) {
            slideRightOut('upload');
            slideRightIn(activeFolder.id);
            setUploadingFiles(false);
        } else if (activeFolder.folder_id !== null) {
            setActiveFolderId(activeFolder.folder_id);
            slideRightOut(activeFolder.id);
            slideRightIn(activeFolder.folder_id);
        }
    }

    const handleUploadFiles = () => {
        slideLeftOut(activeFolder.id);
        slideLeftIn('upload');
        setUploadingFiles(true);
    }

    const toggleFile = file => {
        if (selectedFile?.id === file.id) setSelectedFile(null);
        else setSelectedFile(file);
    }

    const addNewFiles = (e) => {

        const inputFiles = [...e.target.files];
        const newErrors = {};

        inputFiles.forEach((file, i) => {

            if (file.size > 10485760) {
                newErrors[i] = 'The file size is too large. Please use files less than 10MB.';
                return;
            }

            const extension = FileTypes.find(obj => obj.mime == file.type)?.ext;

            if (! extension) {
                newErrors[i] = 'This file type is not supported.';
                return;
            }

        });

        setFiles(files.concat(inputFiles));
        setErrors({...errors, ...newErrors});
    }

    const removeNewFile = i => {
        const nf = [...files];

        nf.splice(i, 1);

        setFiles(nf);

        if (errors[i]) {
            const newErrors = {...errors}

            newErrors.splice(i, 1);

            setErrors(newErrors);
        }
    }

    // Posters -------------------

    const postFiles = () => {
        if (files.length > 0) {

            const token = api.defaults.headers.common['Authorization'];

            setFilesLeftToBeUploaded(files.length);
            setLoading(true);

            files.forEach((file, i) => {

                if (file.size > 10485760) return;

                const extension = FileTypes.find(obj => obj.mime == file.type)?.ext;

                if (! extension) return;

                VaporExtension.store(file, {
                    progress: progress => handleUploadProgress(Math.round(progress * 100), i)
                }, token).then(response => {
                    api.post('/admin/file', {
                        uuid: response.uuid,
                        url: response.url,
                        key: response.key,
                        extension: extension,
                        bucket: response.bucket,
                        name: file.name,
                        group_id: null,
                        content_type: file.type,
                        size: file.size,
                        folder_id: activeFolder.id,
                        visibility: visibility,
                    }).catch(error => {
                        handleUploadProgress(0, i);

                        if (error?.response?.data?.errors) {
                            setErrors({...errors, [i]: error.response.data.errors[0]})
                        }
                    }).finally(() => {
                        setFilesLeftToBeUploaded(filesLeftToBeUploaded - 1);
                    });
                }).catch(() => {
                    handleUploadProgress(0, i);
                    setFilesLeftToBeUploaded(filesLeftToBeUploaded - 1);
                    setErrors({...errors, [i]: 'Unable to upload file. Please try again later.'})
                });
            });
        }
    }

    const handleUploadProgress = (val, i) => {
        const newUploadProgress = {...uploadProgress};

        newUploadProgress[i] = val;

        setUploadProgress(newUploadProgress);
    }

    useEffect(() => {
        if (filesLeftToBeUploaded < 1) {
            if (errors.length > 0) {
                setLoading(false);
            } else {
                handleFolderBack();
                getFilesAndFolders();
            }
        }
    }, [filesLeftToBeUploaded])

    return (
        <Dialog
            aria-labelledby="select-file-dialog-title"
            open={open}
            fullWidth
            maxWidth="xs"
            onClose={onClose}
            onExited={handleExit}
            >
            <DialogTitle id="select-file-dialog-title" className={classes.dialogTitle} disableTypography>
                <ListItem ContainerComponent="div" className={classes.listItemTitle}>
                    <ListItemIcon>
                        <IconButton
                            aria-label="Back"
                            onClick={handleFolderBack}
                            disabled={((activeFolderId === 0) && !uploadingFiles) || loading}
                            >
                            <LeftIcon fontSize="small"/>
                        </IconButton>
                    </ListItemIcon>
                    <ListItemText primary={activeFolder.name}/>
                    <ListItemSecondaryAction>
                        <IconButton
                            aria-label="Cancel"
                            onClick={onClose}
                            disabled={loading}
                            >
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </ListItemSecondaryAction>
                </ListItem>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <div className={classes.folderContainer}>
                    {folders.map(folder => (
                        <div key={folder.id} id={`move-folder-${folder.id}`} className={classes.folder} style={(folder.id === 0 )? {left: 0} : null}>
                            <Loading isLoading={loading}/>
                            {(folder?.folders?.length || folder?.files?.length) ? (
                                <List>
                                    {folder?.folders?.map(subFolder => (
                                        <FolderListItem
                                            key={'folder-' + subFolder.id}
                                            folder={subFolder}
                                            onOpen={handleFolderNext}
                                        />
                                    ))}
                                    {folder?.files?.map(file => (
                                        <FileListItem
                                            key={'file-' + file.id}
                                            file={file}
                                            selectedFile={selectedFile}
                                            onSelect={toggleFile}
                                            type={type}
                                        />
                                    ))}
                                </List>
                            ) : (
                                <div className={classes.folderEmptyContainer}>
                                    <Typography
                                        color="textSecondary"
                                        variant="subtitle2"
                                        className={classes.folderEmpty}
                                        >
                                        This folder is empty
                                    </Typography>
                                </div>
                            )}
                        </div>
                    ))}
                    <div className={classes.folder} id={`move-folder-upload`}>
                        {files.length ? files.map((file, i) => (
                            <NewFileListItem
                                key={'newfile-' + i}
                                file={file}
                                onRemove={() => removeNewFile(i)}
                                uploadProgress={uploadProgress}
                                index={i}
                                errors={errors}
                            />
                        )) : (
                            <div className={classes.folderEmptyContainer}>
                                <Typography
                                    color="textSecondary"
                                    variant="subtitle2"
                                    className={classes.folderEmpty}
                                    >
                                    Click <strong>OPEN FILES</strong> to add files to upload
                                </Typography>
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
            {selectedFile ? (
                <DialogActions>
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>
                            <Typography variant="subtitle2">
                                {selectedFile.name}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Button
                                onClick={(event) => onSelect(selectedFile, event)}
                                color="primary"
                                variant="contained"
                                disabled={loading}
                                >
                                Select File
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            ) : uploadingFiles ? (
                <DialogActions>
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>
                            <input
                                accept="*/*"
                                className={classes.input}
                                id="file-input"
                                type="file"
                                onChange={addNewFiles}
                                size="60"
                                multiple="multiple"
                                ref={fileInputField}
                            />
                            <label htmlFor="file-input">
                                <Button
                                    color="primary"
                                    variant="outlined"
                                    disabled={loading}
                                    component="span"
                                    >
                                    Open Files
                                </Button>
                            </label>
                        </Grid>
                        <Grid item>
                            <Button
                                onClick={postFiles}
                                color="primary"
                                variant="contained"
                                disabled={loading || files.length < 1}
                                >
                                Upload Files
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            ) : (
                <DialogActions>
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item></Grid>
                        <Grid item>
                            <Button
                                onClick={handleUploadFiles}
                                color="primary"
                                variant="outlined"
                                disabled={loading}
                                >
                                Upload Files
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            )}
        </Dialog>
    );
}
